import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { UncataloguedDetailOut, UncataloguedOut, UncataloguedRow } from '../api/types'
import { PageHeader } from '../components/Layout'
import { FilterBar } from '../components/FilterBar'
import { Kpi } from '../components/Kpi'
import { Pager } from '../components/Pager'
import { Panel } from '../components/States'
import { StatusChip } from '../components/StatusChip'
import { useFilters, usePaging } from '../lib/filters'
import { branchName, count, formatDate, money, rangeLabel } from '../lib/format'

/** The invoices one off-catalogue product appeared on. */
function ProductInvoices({ row, columns }: { row: UncataloguedRow; columns: number }) {
  const { filters } = useFilters({ allTime: true })
  const query = useQuery({
    queryKey: ['uncatalogued-detail', row.key, filters.from, filters.to, filters.outletId, filters.scope],
    queryFn: () =>
      api.get<UncataloguedDetailOut>('/analytics/products/uncatalogued/detail', {
        key: row.key,
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
        scope: filters.scope,
      }),
  })

  const detail = query.data

  return (
    <tr>
      <td colSpan={columns} style={{ background: 'var(--page)', padding: '12px 16px' }}>
        <Panel
          isLoading={query.isPending}
          error={query.error}
          isEmpty={detail?.invoices.length === 0}
          emptyText="No invoices found."
        >
          {detail ? (
            <div className="card">
              <div className="card-body flush">
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th className="num">Date</th>
                        <th>Branch</th>
                        <th>Customer</th>
                        <th>Salesperson</th>
                        <th>Status</th>
                        <th className="num">Qty</th>
                        <th className="num">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.invoices.map((invoice) => (
                        <tr key={invoice.invoice_id}>
                          <td>
                            <Link to={`/invoices/${invoice.invoice_id}`}>
                              {invoice.hike_invoice_number ?? invoice.reference}
                            </Link>
                          </td>
                          <td className="num cell-sub">{formatDate(invoice.date)}</td>
                          <td className="cell-sub" style={{ whiteSpace: 'nowrap' }}>
                            {branchName(invoice.outlet_name)}
                          </td>
                          <td>{invoice.customer_name}</td>
                          <td className="cell-sub">{invoice.sales_person ?? '—'}</td>
                          <td>
                            <StatusChip status={invoice.status} />
                          </td>
                          <td className="num">{count(invoice.qty)}</td>
                          <td className="num">{money(invoice.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="strong">Total</td>
                        <td colSpan={5} />
                        <td className="num strong">{count(detail.total_units)}</td>
                        <td className="num strong">{money(detail.total_revenue)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </Panel>
      </td>
    </tr>
  )
}

export function UncataloguedPage() {
  const { filters } = useFilters({ allTime: true })
  const { offset, limit, setOffset } = usePaging(50)
  const [open, setOpen] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['uncatalogued', filters.from, filters.to, filters.outletId, filters.scope, offset, limit],
    queryFn: () =>
      api.get<UncataloguedOut>('/analytics/products/uncatalogued', {
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
        scope: filters.scope,
        limit,
        offset,
      }),
  })

  const rows = query.data?.items ?? []
  const withoutSku = rows.filter((row) => !row.sku).length

  return (
    <>
      <PageHeader
        title="Sold, but not catalogue products"
        subtitle={rangeLabel(filters.from, filters.to)}
      >
        <Link className="btn" to="/products">
          ← Product performance
        </Link>
      </PageHeader>

      <div className="content stack">
        <FilterBar allTime />

        <div className="alert info">
          These sold under no active catalogue SKU, so no catalogue view can show them and they
          carry no stock or cost data. Most are lines typed without a SKU — custom items, charges,
          ad-hoc entries. A few may be SKUs disabled in HIKE since the sale.
        </div>

        <div className="grid cols-3">
          <Kpi label="Products" value={count(query.data?.total)} />
          <Kpi label="Revenue" value={money(query.data?.total_revenue)} />
          <Kpi
            label="No SKU at all"
            value={count(withoutSku)}
            foot="on this page"
          />
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Off-catalogue products</h2>
            <span className="hint">click a name to see the invoices it appeared on</span>
          </div>
          <div className="card-body flush">
            <Panel
              isLoading={query.isPending}
              error={query.error}
              isEmpty={rows.length === 0}
              emptyText="Everything sold in this period matched a catalogue product."
            >
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th className="num">Revenue</th>
                      <th className="num">Units</th>
                      <th className="num">Invoices</th>
                      <th className="num">Last sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isOpen = open === row.key
                      return [
                        <tr
                          key={row.key}
                          className="clickable"
                          onClick={() => setOpen(isOpen ? null : row.key)}
                        >
                          <td className="strong">
                            <span
                              className="glyph"
                              style={{ marginRight: 6, color: 'var(--text-faint)' }}
                            >
                              {isOpen ? '▾' : '▸'}
                            </span>
                            {row.name}
                          </td>
                          <td className="cell-sub">
                            {row.sku ? (
                              <span className="chip amber">{row.sku} — not active</span>
                            ) : (
                              <span className="chip grey">no SKU</span>
                            )}
                          </td>
                          <td className="num strong">{money(row.revenue)}</td>
                          <td className="num">{count(row.units)}</td>
                          <td className="num">{count(row.invoice_count)}</td>
                          <td className="num cell-sub">{formatDate(row.last_sold)}</td>
                        </tr>,
                        isOpen ? (
                          <ProductInvoices key={`${row.key}-detail`} row={row} columns={6} />
                        ) : null,
                      ]
                    })}
                  </tbody>
                </table>
              </div>
              <Pager offset={offset} limit={limit} total={query.data?.total ?? 0} onChange={setOffset} />
            </Panel>
          </div>
        </div>
      </div>
    </>
  )
}
